import { apiClient, ApiResponse } from './client';
import type { XamanPayloadResponse } from '../types/signing';

// Types
export type PromotionTier = '3_days' | '7_days' | '14_days';

export interface PromotionTierInfo {
  id: PromotionTier;
  durationDays: number;
  priceDeals: number;
  label: string;
  description: string;
}

export interface DealsTokenInfo {
  issuer: string;
  currency: string;
  currencyHex: string;
}

export interface PromotionOptions {
  tiers: PromotionTierInfo[];
  dealsToken: DealsTokenInfo;
  burnAddress: string;
}

export interface TierAffordability {
  affordable: boolean;
  price: number;
  label: string;
  description: string;
}

export interface BalanceResponse {
  address: string;
  balance: number;
  formattedBalance: string;
  affordable: PromotionTier[];
  tiers: Record<PromotionTier, TierAffordability>;
}

export interface PromotionStatus {
  isPromoted: boolean;
  promotion?: {
    id: string;
    expiresAt: string;
    tier: PromotionTier;
    durationDays: number;
    remainingDays: number;
  };
}

export interface CreatePromotionRequest {
  nftTokenId: string;
  tier: PromotionTier;
}

export type XamanPayload = XamanPayloadResponse;

/** Phase 4 signing intent — carries client-signing txJson for joey-wc. */
export interface SigningIntentRef {
  intentId: string;
  provider: string;
  txJson?: Record<string, unknown>;
}

export interface CreatePromotionResponse {
  promotionId: string;
  payload?: XamanPayload;
  signingIntent?: SigningIntentRef;
  tier: PromotionTierInfo;
}

export interface ConfirmPromotionRequest {
  payloadUuid: string;
}

export interface CancelPromotionRequest {
  payloadUuid: string;
}

export interface UserPromotion {
  id: string;
  nft_token_id: string;
  collection_id: string | null;
  user_address: string;
  started_at: string | null;
  expires_at: string;
  duration_days: number;
  amount_paid: number;
  tier: PromotionTier;
  tx_hash: string | null;
  payment_status: 'pending' | 'confirmed' | 'expired' | 'cancelled';
  created_at: string;
  nft_name?: string;
  nft_thumbnail_url?: string;
  collection_name?: string;
}

// API Functions

/**
 * Get promotion options and pricing tiers
 */
export async function getPromotionOptions(): Promise<ApiResponse<PromotionOptions>> {
  try {
    return await apiClient.get<ApiResponse<PromotionOptions>>('/promotions/options');
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get promotion options',
    };
  }
}

/**
 * Get DEALS token balance for an address
 */
export async function getDealsBalance(address: string): Promise<ApiResponse<BalanceResponse>> {
  try {
    return await apiClient.get<ApiResponse<BalanceResponse>>(`/promotions/balance/${address}`);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get DEALS balance',
    };
  }
}

/**
 * Check if an NFT is currently promoted
 */
export async function getPromotionStatus(nftTokenId: string): Promise<ApiResponse<PromotionStatus>> {
  try {
    return await apiClient.get<ApiResponse<PromotionStatus>>(`/promotions/status/${nftTokenId}`);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get promotion status',
    };
  }
}

/**
 * Create a promotion payment request (requires authentication)
 */
export async function createPromotion(
  data: CreatePromotionRequest
): Promise<ApiResponse<CreatePromotionResponse>> {
  try {
    return await apiClient.post<ApiResponse<CreatePromotionResponse>>('/promotions/create', data);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create promotion',
    };
  }
}

/**
 * Confirm a promotion after successful payment
 */
export async function confirmPromotion(
  data: ConfirmPromotionRequest
): Promise<ApiResponse<{ promotion: UserPromotion }>> {
  try {
    return await apiClient.post<ApiResponse<{ promotion: UserPromotion }>>(
      '/promotions/confirm',
      data
    );
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to confirm promotion',
    };
  }
}

/**
 * Cancel a pending promotion
 */
export async function cancelPromotion(
  data: CancelPromotionRequest
): Promise<ApiResponse<{ cancelled: boolean }>> {
  try {
    return await apiClient.post<ApiResponse<{ cancelled: boolean }>>('/promotions/cancel', data);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to cancel promotion',
    };
  }
}

/**
 * Get user's promotion history (requires authentication)
 */
export async function getUserPromotions(): Promise<ApiResponse<{ promotions: UserPromotion[] }>> {
  try {
    return await apiClient.get<ApiResponse<{ promotions: UserPromotion[] }>>('/promotions/user');
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get user promotions',
    };
  }
}

/**
 * Get all promoted NFT token IDs for a collection
 */
export async function getCollectionPromotedNFTs(
  collectionId: string
): Promise<ApiResponse<{ collectionId: string; promotedNFTs: string[]; count: number }>> {
  try {
    return await apiClient.get<
      ApiResponse<{ collectionId: string; promotedNFTs: string[]; count: number }>
    >(`/promotions/collection/${collectionId}`);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get collection promoted NFTs',
    };
  }
}

/**
 * Format DEALS amount for display
 */
export function formatDealsAmount(amount: number): string {
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(0)}K`;
  }
  return amount.toLocaleString();
}
